<template>
  <div class="container-fluid div-title py-2">
    <div class="div-title cursor-pointer" @click="brandClickedInflate">
      <img class="img-style-inflate" :src="inflateBrandImage" alt="logo"/>
    </div>
    <div class="row hero-grid">
      <div v-for="(product, index) in products" :key="`p-${index}`" class="col-12 col-md-6 mb-3" :class="{ 'position-relative': index === 1 }">
        <!-- Inflate Fun tile (shown for index === 0). For Active Brands (index === 1) the tile is temporarily hidden and a banner is shown instead. To restore Active Brands product tile: change v-if below to remove the `index !== 1` guard, remove the banner block below, and uncomment the brand logo overlay further down. -->
        <div v-if="index !== 1" class="tile" @click="goToProduct(index)">
          <img class="tile-img" :src="url + product.photos[0]" alt=""/>
          <div class="tile-gradient"/>
          <div class="tile-meta">
            <div class="meta-left">
              <span class="meta-title">{{ product?.name || '' }}</span>
            </div>
            <div class="meta-right">
              <span class="cta">{{ $t('viewProduct') }}</span>
            </div>
          </div>
        </div>
        <!-- TEMPORARY: Active Brands banner replacement (links to https://activebrands.pro/). Remove this block to restore the product tile. -->
        <a v-if="index === 1" href="https://activebrands.pro/" target="_blank" rel="noopener noreferrer" class="active-brands-banner">
          <img class="banner-img" :src="require('@/assets/shop/active-brands-banner.jpg')" alt="Active Brands"/>
        </a>
        <!-- Active brand div after second product (index === 1) -->
        <!-- TEMPORARILY HIDDEN: brand logo overlay (uncomment together with restoring the tile above)
        <div v-if="index === 1" class="cursor-pointer" @click="brandClickedActive">
          <img class="img-style-active" :src="activeBrandImage" alt="logo"/>
        </div>
        -->
      </div>
      <div v-for="(videoUrl, vIndex) in videos" :key="`v-${vIndex}`" class="col-6 col-md-3 mb-3">
        <div class="tile tile-vertical" @click="openVideo(videoUrl)">
          <div v-if="!isThumbnailLoaded(vIndex)" class="video-skeleton">
             <div class="skeleton-content">
               <div class="skeleton-play-icon">▶</div>
               <div class="skeleton-text">{{ $t('videoLoading') }}</div>
             </div>
          </div>
          <img 
            v-show="isThumbnailLoaded(vIndex)"
            class="tile-img tile-img-loaded" 
            :src="getVideoPoster(vIndex)" 
            alt="video"
            @load="onVideoThumbnailLoad(vIndex)"
            @error="onVideoThumbnailError(vIndex)"
          />
          <img 
            v-if="getVideoPoster(vIndex) && !isThumbnailLoaded(vIndex)"
            style="display: none;"
            :src="getVideoPoster(vIndex)" 
            @load="onVideoThumbnailLoad(vIndex)"
            @error="onVideoThumbnailError(vIndex)"
          />
          <div class="tile-gradient"/>
          <div class="tile-meta">
            <div class="meta-left">
              <span class="meta-badge">{{ $t('videoBadge') }}</span>
            </div>
            <div class="meta-right">
              <span class="play-badge" aria-hidden="true">▶</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showVideo" class="video-modal" @click.self="closeVideo">
      <div class="video-modal-content" :class="{ tiktok: isTikTok }">
        <img class="video-close" src="/img/close.8df2fa7e.svg" alt="Close" @click="closeVideo" />
        <iframe
          v-if="embedSrc"
          :src="embedSrc"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowfullscreen
        ></iframe>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
export default {
  name: 'HeroGrid',
  props: {
    products: {
      type: Array,
      default: () => []
    },
    videos: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      url: process.env.VUE_APP_URL_ROOT,
      baseUrl: process.env.VUE_APP_FRONT,
      showVideo: false,
      embedSrc: null,
      isTikTok: false,
      videoThumbs: [],
      videoThumbnailsLoaded: []
    }
  },
  computed: {
    activeBrandImage() {
      // English and Portuguese -> heroFormActive.png
      // Spanish -> factoryplay_activacion_marcas_.png
      return this.$i18n.locale === 'es' 
        ? require('@/assets/shop/factoryplay_activacion_marcas_.png')
        : require('@/assets/shop/heroFormActive.png');
    },
    inflateBrandImage() {
      // English and Portuguese -> heroFormInflate.png
      // Spanish -> factoryplay_hinchables_diversion.png
      return this.$i18n.locale === 'es'
        ? require('@/assets/shop/factoryplay_hinchables_diversion.png')
        : require('@/assets/shop/heroFormInflate.png');
    }
  },
  mounted() {
    this.preloadVideoThumbnails();
    document.addEventListener('keydown', this.onKeydownEsc);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydownEsc);
  },
  methods: {
    ...mapActions('account', ['updateBrand']),
    goToProduct(index) {
      const slug = this.products[index].slug;
      const href = `${this.baseUrl}/Product/${slug}`;
      window.location.href = href;
    },
    getVideoPoster(vIndex) {
      const fetched = this.videoThumbs[vIndex];
      if (fetched) return fetched;
      return null;
    },
    isThumbnailLoaded(vIndex) {
      return this.videoThumbnailsLoaded[vIndex] === true;
    },
    onVideoThumbnailLoad(vIndex) {
      this.$set(this.videoThumbnailsLoaded, vIndex, true);
    },
    onVideoThumbnailError(vIndex) {
      this.$set(this.videoThumbnailsLoaded, vIndex, false);
    },
    async preloadVideoThumbnails() {
      if (!this.videos || !this.videos.length) return;
      
      this.videoThumbnailsLoaded = new Array(this.videos.length).fill(false);
      
      const promises = this.videos.map(v => this.resolveThumbnail(v).catch(() => null));
      const results = await Promise.all(promises);
      this.videoThumbs = results;
    },
    async resolveThumbnail(link) {
      if (!link) return null;
      try {
        const url = new URL(link);
        const host = url.hostname.replace(/^www\./, '');
        if (host === 'tiktok.com') {
          const oembed = await fetch(`https://www.tiktok.com/oembed?url=${encodeURIComponent(link)}`);
          const data = await oembed.json();
          return data && data.thumbnail_url ? data.thumbnail_url : null;
        }
        if (host === 'youtube.com' || host === 'm.youtube.com') {
          const id = url.searchParams.get('v');
          if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
        }
        if (host === 'youtu.be') {
          const id = url.pathname.replace(/^\//, '');
          if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
        }
      } catch (e) { return null; }
      return null;
    },
    openVideo(link) {
      this.embedSrc = this.embedLink(link);
      if (!this.embedSrc) {
        if (/^https:\/\//i.test(link || '')) window.open(link, '_blank', 'noopener');
        return;
      }
      this.isTikTok = this.isTikTokLink(link);
      this.showVideo = true;
      try { document.body.style.overflow = 'hidden'; } catch (e) { return; }
    },
    closeVideo() {
      this.showVideo = false;
      this.embedSrc = null;
      this.isTikTok = false;
      try { document.body.style.overflow = ''; } catch (e) { return; }
    },
    onKeydownEsc(e){ if (e.key === 'Escape') this.closeVideo(); },
    embedLink(link) {
      if (!link || typeof link !== 'string') return link;
      try {
        const url = new URL(link);
        const host = url.hostname.replace(/^www\./, '');
        if (host === 'youtube.com' || host === 'm.youtube.com') {
          const videoId = url.searchParams.get('v');
          if (videoId) return `https://www.youtube.com/embed/${videoId}`;
        }
        if (host === 'youtu.be') {
          const videoId = url.pathname.replace(/^\//, '');
          if (videoId) return `https://www.youtube.com/embed/${videoId}`;
        }
        if (host === 'tiktok.com') {
          const parts = url.pathname.split('/').filter(Boolean);
          const videoIndex = parts.findIndex(p => p === 'video');
          if (videoIndex !== -1 && parts[videoIndex + 1]) {
            const videoId = parts[videoIndex + 1];
            return `https://www.tiktok.com/embed/v2/${videoId}`;
          }
        }
      } catch (e) { return null; }
      // Only YouTube/TikTok embeds are framed. The links come from the API (key values), so anything else
      // (e.g. a javascript: URL, which runs in this origin when set as an iframe src) is never used.
      return null;
    },
    isTikTokLink(link){
      try { const u = new URL(link); return u.hostname.replace(/^www\./,'') === 'tiktok.com'; } catch(e){ return false; }
    },
    brandClickedInflate() {
      const brand = 'InflateYourImagination';
      this.updateBrand(brand);
      this.$router.push({name: 'Shop', query: {brand}});
    },
    brandClickedActive() {
      const brand = 'ActiveBrands';
      this.updateBrand(brand);
      this.$router.push({name: 'Shop', query: {brand}});
    }
  }
}
</script>

<style scoped>
.div-title{
  position: relative;
}
.hero-grid{ /* spacing is controlled by Bootstrap gutters + mb-3 */ min-height: 0; }
.tile{
  position: relative;
  width: 100%;
  height: auto;
  aspect-ratio: 16/7;
  overflow: hidden;
  /* border-radius: 14px; */
  cursor: pointer;
  background: #0d0d0d;
  box-shadow: 0 2px 18px rgba(0,0,0,0.08);
  transition: transform .25s ease, box-shadow .25s ease;
}
.tile-vertical{ aspect-ratio: 9/13; }
.tile-img{
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform .6s cubic-bezier(.2,.6,.2,1);
}
.tile-img-loaded{
  animation: fadeIn 0.3s ease-in-out;
}
.tile:hover{ transform: translateY(-3px); box-shadow: 0 16px 32px rgba(0,0,0,0.18); }
.tile:hover .tile-img{ transform: scale(1.05); }
.tile-gradient{
  position: absolute;
  left: 0; right: 0; bottom: 0;
  height: 40%;
  background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,.55) 100%);
}
.tile-meta{
  position: absolute;
  left: 12px; right: 12px; bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: white;
}
.meta-left{ min-width: 0; }
.meta-title{
  font-weight: 600;
  font-size: 14px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.meta-badge{
  font-size: 12px;
  background: rgba(255,255,255,.16);
  border: 1px solid rgba(255,255,255,.28);
  padding: 4px 8px;
  border-radius: 999px;
}
.meta-right{ display: flex; align-items: center; gap: 8px; }
.cta{
  background: white;
  color: #111;
  border-radius: 999px;
  font-size: 12px;
  padding: 6px 10px;
}
.play-badge{
  background: rgba(255,255,255,0.95);
  color: #111;
  border-radius: 999px;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}
@media (max-width: 767px){ }

.video-modal{
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.8);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.video-modal-content{
  position: relative;
  width: min(960px, 90vw);
  aspect-ratio: 16/7;
  background: black;
  border-radius: 12px;
  box-shadow: 0 12px 48px rgba(0,0,0,0.5);
}
.video-modal-content.tiktok{
  width: auto;
  height: min(90vh, 720px);
  aspect-ratio: 9/13;
}
.video-modal-content iframe{
  width: 100%;
  height: 100%;
}
.video-close{
  position: fixed;
  top: 20px;
  right: 20px;
  width: 34px;
  height: 34px;
  cursor: pointer;
  filter: brightness(0) invert(1);
  transition: opacity 0.2s ease;
  z-index: 1001;
}
.video-close:hover{
  opacity: 0.7;
}

/* Active Brands banner (temporary replacement for the second hero tile). Remove together with the banner block in the template to restore. */
.active-brands-banner{
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 16/7;
  cursor: pointer;
  text-decoration: none;
  overflow: hidden;
  box-shadow: 0 2px 18px rgba(0,0,0,0.08);
  transition: transform .25s ease, box-shadow .25s ease;
}
.active-brands-banner:hover{
  transform: translateY(-3px);
  box-shadow: 0 16px 32px rgba(0,0,0,0.18);
}
.banner-img{
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.img-style-inflate{
  position: absolute;
  z-index: 2;
  height: 75px;
  top: 20px;
}
.img-style-active{
  position: absolute;
  z-index: 2;
  height: 75px;
  top: 20px;
  left: 11px;
}
@media (max-width: 991px){
  .img-style-inflate{ top: 10px; height: 50px; }
  .img-style-active{ top: 10px; height: 50px; }
}

/* Video Skeleton Loading */
.video-skeleton {
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.skeleton-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #666;
}

.skeleton-play-icon {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #333;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.skeleton-text {
  font-size: 12px;
  font-weight: 500;
  opacity: 0.7;
}

@keyframes skeleton-loading {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>


